import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Folder } from "lucide-react";

import data from "../../../_data/solutionsandinsights.json";
import { loadRecents, saveRecents, fetchIndex } from "../lib/solutions.js";

const PER_PAGE = 6;

function humanizeRepoName(repoKey) {
  const parts = String(repoKey || "").split("/");
  const repo = parts[2] || repoKey || "Unknown";
  return repo
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCount(n) {
  const num = Number(n || 0);
  return `${num} file${num === 1 ? "" : "s"}`;
}

function isDirectVideoUrl(u) {
  return /\.(mp4|webm|ogg)(\?|#|$)/i.test(String(u || ""));
}

function CardMedia({ video, videoEmbed, image, title }) {
  const [failed, setFailed] = React.useState(false);
  const vidRef = React.useRef(null);
  const embedRef = React.useRef(null);

  const directVideo = isDirectVideoUrl(video);

  React.useEffect(() => {
    const el = embedRef.current;
    if (!el) return;

    const handleError = () => setFailed(true);
    el.addEventListener("error", handleError, true);
    return () => el.removeEventListener("error", handleError, true);
  }, [videoEmbed]);

  const play = async () => {
    if (!directVideo || failed) return;
    const el = vidRef.current;
    if (!el) return;

    try {
      el.currentTime = 0;
      el.muted = true;
      el.playsInline = true;
      el.load();
      const p = el.play();
      if (p && typeof p.then === "function") await p;
    } catch {}
  };

  const stop = () => {
    if (!directVideo || failed) return;
    const el = vidRef.current;
    if (!el) return;

    try {
      el.pause();
      el.currentTime = 0;
    } catch {}
  };

  // keep this only if you truly pass embed HTML (not just a URL)
  if (videoEmbed && !failed && /<[^>]+>/.test(String(videoEmbed))) {
    return (
      <div
        ref={embedRef}
        className="si-cardThumb si-embed"
        dangerouslySetInnerHTML={{ __html: videoEmbed }}
      />
    );
  }

  // If video is not a direct file, show image
  if (!video || failed || !directVideo) {
    return <img src={image} alt="" className="si-cardThumb" loading="lazy" />;
  }

  // Hover handlers on wrapper so hover always triggers
  return (
    <div className="si-cardMedia" onMouseEnter={play} onMouseLeave={stop}>
      <video
        ref={vidRef}
        className="si-cardThumb"
        muted
        playsInline
        preload="metadata"
        poster={image}
        onError={() => setFailed(true)}
      >
        <source src={video} />
      </video>
    </div>
  );
}

export default function SolutionsAndInsights() {
  const hero = data?.hero;
  const gallery = data?.projectsGallery;

  const [query, setQuery] = useState("");
  const [indexItems, setIndexItems] = useState([]);
  const [recents, setRecents] = useState(() => loadRecents());

  const [page, setPage] = useState(1);

  useEffect(() => {
    let alive = true;
    (async () => {
      const items = await fetchIndex();
      if (!alive) return;
      setIndexItems(items);
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const refreshRecents = () => setRecents(loadRecents());

    window.addEventListener("pageshow", refreshRecents);
    window.addEventListener("focus", refreshRecents);

    const onVis = () => {
      if (!document.hidden) refreshRecents();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("pageshow", refreshRecents);
      window.removeEventListener("focus", refreshRecents);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const repoCounts = useMemo(() => {
    const map = new Map();
    for (const it of indexItems) {
      const repoKey = it.repo || "";
      if (!repoKey) continue;
      map.set(repoKey, (map.get(repoKey) || 0) + 1);
    }
    return map;
  }, [indexItems]);

  const cards = useMemo(() => {
    const yamlCards = Array.isArray(gallery?.cards) ? gallery.cards : [];
    return yamlCards.map((c) => {
      const repoKey = c.repoKey || "";
      const computedCount = repoCounts.get(repoKey) || 0;
      const filesCount = Number(c.filesCount ?? computedCount);

      return {
        repoKey,
        repoSlug: c.repoSlug || "",
        title: c.title || humanizeRepoName(repoKey),
        video: c.video || "",
        videoEmbed: c.videoEmbed || "",
        image: c.image || "/assets/img/hero-image.png",
        filesCount,
      };
    });
  }, [gallery, repoCounts]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cards;
    return cards.filter((c) => {
      if ((c.title || "").toLowerCase().includes(q)) return true;
      if ((c.repoKey || "").toLowerCase().includes(q)) return true;
      return false;
    });
  }, [cards, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  }, [filtered.length]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filtered.slice(start, start + PER_PAGE);
  }, [filtered, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const recentCards = useMemo(() => {
    if (!recents.length) return [];
    const lookup = new Map(cards.map((c) => [c.repoKey, c]));
    return recents
      .map((k) => lookup.get(k))
      .filter(Boolean)
      .slice(0, 2);
  }, [cards, recents]);

  function markRecent(repoKey) {
    if (!repoKey) return;
    const next = [repoKey, ...recents.filter((x) => x !== repoKey)];
    setRecents(next);
    saveRecents(next);
  }

  function openRepo(card) {
    markRecent(card.repoKey);
  }

  if (!hero || !gallery) {
    return (
      <section className="si">
        <div className="si-empty">Missing solutionsandinsights.yml data.</div>
      </section>
    );
  }

  return (
    <section className="si">
      <div className="hero">
        <div className="heroLeft">
          <h1 className="heroTitle">{hero.title}</h1>
          <p className="heroDesc">{hero.body}</p>

          <div className="heroRight" aria-hidden="true">
            <div
              className="heroArt"
              style={{ backgroundImage: `url(${hero.heroImage || "/assets/img/hero-image.png"})` }}
              role="img"
              aria-label={hero.heroImageAlt || "Hero art"}
            />
          </div>
        </div>

        <div className="hero-patterns" aria-hidden="true">
          <img src="/assets/img/banner.png" alt="" className="hero-pattern-image" />
        </div>
      </div>

      <div className="si-panel">
        <div className="si-panelHeader">
          <div className="si-panelTitle">{gallery.title}</div>

          <div className="si-panelRight">
            <div className="si-search">
              <span className="si-searchIcon" aria-hidden="true" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={gallery.searchPlaceholder || "Label"}
                aria-label="Search"
              />
            </div>

            <a className="si-advanced" href="#filters" onClick={(e) => e.preventDefault()}>
              Advanced filters
            </a>
          </div>
        </div>

        <div className="si-sub">
          <div className="si-subTitle">Recently Opened ({recentCards.length})</div>
          <div className="si-grid si-grid-3">
            {recentCards.map((c) => (
              <ProjectCard key={`r-${c.repoKey}`} card={c} onOpen={() => openRepo(c)} />
            ))}
            {!recentCards.length && (
              <div className="si-emptyState">No recent projects yet. Click a project card.</div>
            )}
          </div>
        </div>

        <div className="si-sub">
          <div className="si-subTitle">
            All Projects ({filtered.length})
            <span className="si-subMeta">
              · Page {page} / {totalPages}
            </span>
          </div>

          <div className="si-grid si-grid-3">
            {pageItems.map((c) => (
              <ProjectCard key={c.repoKey} card={c} onOpen={() => openRepo(c)} />
            ))}
            {!filtered.length && (
              <div className="si-emptyState">No projects match your search.</div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="si-pager">
              <button
                className="si-pagerBtn"
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Prev
              </button>

              <div className="si-pagerInfo">
                Page {page} / {totalPages}
              </div>

              <button
                className="si-pagerBtn"
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ProjectCard({ card, onOpen }) {
  const href =
    "/solutions/view/?" +
    `repoKey=${encodeURIComponent(card.repoKey)}` +
    `&repoSlug=${encodeURIComponent(card.repoSlug || "")}` +
    `&title=${encodeURIComponent(card.title || "")}` +
    `&videoEmbed=${encodeURIComponent(card.videoEmbed || "")}`;

  return (
    <Link className="si-card si-cardLinkWrap" to={href} onClick={() => onOpen?.(card)}>
      <div className="si-cardImg">
        <CardMedia
          video={card.video}
          videoEmbed={card.videoEmbed}
          image={card.image}
          title={card.title}
        />
      </div>

      <div className="si-cardInfo">
        <div className="si-cardTitle">{card.title}</div>
        <div className="si-cardMeta">
          <Folder className="si-folderIcon" aria-hidden="true" />
          <span>{formatCount(card.filesCount)}</span>
        </div>
      </div>
    </Link>
  );
}
